import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import {
  ShoppingBag, TicketCheck, Landmark, Luggage, Info,
  UtensilsCrossed, Armchair, Bath, HeartPulse, MoreHorizontal,
  Plane, Search as SearchIcon, MapPin, Layers, Plus, Minus, X,
  Home as HomeIcon, ChevronDown, ChevronUp, QrCode, Globe,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import MapView from "@/components/MapView";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { useI18n } from "@/context/I18nContext";

// Icon map keeps the backend registry driving the UI without shipping
// React components over the wire.
const ICON_MAP = {
  ShoppingBag, TicketCheck, Landmark, Luggage, Info,
  UtensilsCrossed, Armchair, Bath, HeartPulse, MoreHorizontal,
};

// Language metadata for the kiosk header switcher. Emoji flags are cross-
// platform and don't need font files. If a venue advertises a language we
// don't have metadata for, we fall back to a plain code label.
const LANG_META = {
  en: { flag: "🇬🇧", label: "English"  },
  vi: { flag: "🇻🇳", label: "Tiếng Việt" },
  hi: { flag: "🇮🇳", label: "हिन्दी"    },
  ar: { flag: "🇦🇪", label: "العربية"  },
  ko: { flag: "🇰🇷", label: "한국어"    },
  ja: { flag: "🇯🇵", label: "日本語"    },
  zh: { flag: "🇨🇳", label: "中文"      },
  fr: { flag: "🇫🇷", label: "Français"  },
  de: { flag: "🇩🇪", label: "Deutsch"   },
  es: { flag: "🇪🇸", label: "Español"   },
  ru: { flag: "🇷🇺", label: "Русский"   },
};

/**
 * /venue/:slug — Universal Venue Map (Slice H).
 *
 * Layout modes
 *   · Visitor (default) — responsive mobile-first with a bottom-sheet toolbar
 *   · Kiosk    (?kiosk=1) — fullscreen, no app chrome, big controls
 *
 * URL params
 *   ?floor=<floor_id>    · active floor
 *   ?cat=<category_key>  · active category filter
 *   ?q=<name>            · search filter
 *   ?here=<poi_id>       · "you are here" marker (kiosk installer sets this)
 *   ?kiosk=1             · kiosk mode
 */
export default function Venue() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [venue, setVenue] = useState(null);
  const [pois, setPois] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [selected, setSelected] = useState(null);  // clicked POI
  const [error, setError] = useState(null);

  const floorId = params.get("floor") || "";
  const activeCat = params.get("cat") || "";
  const q = params.get("q") || "";
  const hereId = params.get("here") || "";
  const kiosk = params.get("kiosk") === "1";
  const lang = params.get("lang") || (typeof localStorage !== "undefined" ? localStorage.getItem("f2n_venue_lang") || "en" : "en");
  const [showQr, setShowQr] = useState(false);
  const { setLang, t } = useI18n();
  useEffect(() => { try { localStorage.setItem("f2n_venue_lang", lang); } catch { /* ignore */ } setLang(lang); }, [lang, setLang]);

  useSeo({
    title: venue ? `${venue.name} — Find2Near map` : "Venue map — Find2Near",
    description: venue
      ? `Interactive floor map of ${venue.name}${venue.city ? ` in ${venue.city}` : ""} — find shops, restaurants, ATMs, check-in counters and more.`
      : "Universal venue floor plan — airports, malls, hospitals, schools.",
  });

  // Load meta once (category registry).
  useEffect(() => {
    api.get("/venues/meta/categories")
      .then(r => setCats(r.data?.categories || []))
      .catch(() => setCats([]));
  }, []);

  // Load venue detail + POIs whenever slug / filter changes.
  useEffect(() => {
    setLoading(true);
    setError(null);
    api.get(`/venues/${slug}`)
      .then(r => {
        setVenue(r.data);
        // Default to the highest-ordered floor with an image.
        if (!floorId && r.data?.floors?.length) {
          const withImg = [...r.data.floors].sort((a, b) => (b.order || 0) - (a.order || 0)).find(f => f.image_url) || r.data.floors[0];
          setParams(new URLSearchParams({ ...Object.fromEntries(params), floor: withImg.id }), { replace: true });
        }
      })
      .catch((e) => setError(e.response?.status === 404 ? "not_found" : "load_failed"))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!venue) return;
    const p = { limit: 300 };
    if (floorId)   p.floor    = floorId;
    if (activeCat) p.category = activeCat;
    if (q)         p.q        = q;
    api.get(`/venues/${slug}/pois`, { params: p })
      .then(r => setPois(r.data?.items || []))
      .catch(() => setPois([]));
  }, [venue, slug, floorId, activeCat, q]);

  const activeFloor = useMemo(
    () => venue?.floors?.find(f => f.id === floorId) || venue?.floors?.[0],
    [venue, floorId],
  );

  const setP = (patch) => {
    const np = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => v ? np.set(k, v) : np.delete(k));
    setParams(np);
  };

  // -------------------- render helpers --------------------
  const renderPoiIcon = (poi) => {
    const meta = cats.find(c => c.key === poi.category);
    const Ico  = ICON_MAP[poi.icon || meta?.icon] || MoreHorizontal;
    return <Ico size={12} />;
  };

  if (loading) {
    return (
      <div className={`${kiosk ? "min-h-screen" : "min-h-[60vh]"} grid place-items-center`} data-testid="venue-loading">
        <div className="text-stone-500">Loading map…</div>
      </div>
    );
  }
  if (error === "not_found") {
    return (
      <div className="max-w-xl mx-auto py-20 text-center px-6" data-testid="venue-404">
        <MapPin size={40} className="mx-auto text-stone-300 mb-3" />
        <div className="heading font-bold text-2xl text-stone-900">Venue not found</div>
        <div className="text-sm text-stone-500 mt-2">
          The venue “<b>{slug}</b>” isn’t on Find2Near yet.
        </div>
        <Link to="/venues" className="mt-6 inline-flex rounded-full bg-[#E05E36] hover:bg-[#c94e2a] text-white text-sm font-bold px-5 py-2" data-testid="venue-back-list">
          Browse venues →
        </Link>
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-xl mx-auto py-20 text-center px-6" data-testid="venue-error">
        <div className="heading font-bold text-2xl text-stone-900">Could not load this map</div>
        <div className="text-sm text-stone-500 mt-2">Please try again in a moment.</div>
      </div>
    );
  }

  const wrapCls = kiosk
    ? "fixed inset-0 bg-white z-[9000] flex flex-col"
    : "max-w-7xl mx-auto px-3 md:px-6 py-4";

  return (
    <div className={wrapCls} data-testid={kiosk ? "venue-kiosk" : "venue-visitor"}>
      {/* Header */}
      <div className={`flex items-center gap-2 ${kiosk ? "px-4 py-3 border-b border-stone-200 bg-white" : "mb-3"}`}>
        {!kiosk && (
          <Link to="/venues" className="p-2 -ml-2 rounded-full hover:bg-stone-100" data-testid="venue-back">
            <HomeIcon size={18} className="text-stone-600" />
          </Link>
        )}
        <div className="flex-1 min-w-0">
          <div className="heading font-bold text-lg md:text-xl text-stone-900 truncate flex items-center gap-2">
            {venue.kind === "airport" && <Plane size={18} className="text-[#E05E36]" />}
            {venue.name}
          </div>
          {venue.city && (
            <div className="text-xs text-stone-500 flex items-center gap-1">
              <MapPin size={11} /> {venue.city}{venue.country ? `, ${venue.country}` : ""}
              {activeFloor && <span>· {activeFloor.label}</span>}
            </div>
          )}
        </div>
        {/* Language flag switcher (only when venue advertises ≥1 language) */}
        {venue.languages?.length > 0 && (
          <LangSwitcher
            languages={venue.languages}
            active={lang}
            onPick={(k) => setP({ lang: k })}
          />
        )}
        {/* Kiosk-only: Map On Phone QR handoff */}
        {kiosk && (
          <button
            onClick={() => setShowQr(true)}
            className="text-xs font-bold px-3 py-1.5 rounded-full border bg-white border-stone-200 inline-flex items-center gap-1.5"
            data-testid="venue-qr-btn"
          >
            <QrCode size={12} /> {t("venue.map_on_phone", "Map on phone")}
          </button>
        )}
        {/* Floor selector */}
        {venue.floors?.length > 1 && (
          <FloorPicker
            floors={venue.floors}
            activeId={activeFloor?.id}
            onPick={(id) => setP({ floor: id })}
          />
        )}
      </div>

      {/* Category toolbar (Da-Nang-style) — horizontal scroll on mobile */}
      <div className={`flex gap-2 overflow-x-auto no-scrollbar ${kiosk ? "px-4 py-3 border-b border-stone-100" : "mb-3"}`} data-testid="venue-cat-strip">
        <button
          onClick={() => setP({ cat: "" })}
          className={`text-xs font-bold px-3 py-1.5 rounded-full border whitespace-nowrap ${!activeCat ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-200 text-stone-700"}`}
          data-testid="cat-all"
        >
          {t("venue.all", "All")}
        </button>
        {cats.map(c => {
          const Ico = ICON_MAP[c.icon] || MoreHorizontal;
          const on = activeCat === c.key;
          // Translated category label if the i18n table has one (poi.<key>),
          // otherwise fall back to the backend's English label.
          const label = t(`poi.${c.key}`, c.label);
          return (
            <button
              key={c.key}
              onClick={() => setP({ cat: c.key })}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 whitespace-nowrap transition-colors ${on ? "bg-[#E05E36] text-white border-[#E05E36]" : "bg-white border-stone-200 text-stone-700 hover:border-stone-300"}`}
              data-testid={`cat-${c.key}`}
            >
              <Ico size={12} /> {label}
            </button>
          );
        })}
      </div>

      {/* Search box */}
      <div className={`flex items-center gap-2 bg-stone-50 rounded-xl px-3 py-2 ${kiosk ? "mx-4 my-2" : "mb-3"}`}>
        <SearchIcon size={14} className="text-stone-500" />
        <input
          key={q}
          defaultValue={q}
          onKeyDown={e => e.key === "Enter" && setP({ q: e.currentTarget.value })}
          placeholder={t("venue.search_ph", "Search shops, gates, ATMs…")}
          className="bg-transparent w-full outline-none text-sm"
          data-testid="venue-search-input"
        />
        {q && (
          <button onClick={() => setP({ q: "" })} className="text-stone-400" data-testid="venue-search-clear">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Map canvas */}
      <div className={`relative flex-1 min-h-[420px] rounded-2xl border border-stone-200 bg-stone-50 overflow-hidden ${kiosk ? "mx-4 mb-4" : ""}`} data-testid="venue-canvas">
        {/* Floor image */}
        <div
          className="absolute inset-0 transition-transform origin-center"
          style={{ transform: `scale(${zoom})` }}
          data-testid="venue-floor-layer"
        >
          {activeFloor?.image_url ? (
            <img
              src={activeFloor.image_url}
              alt={activeFloor.label}
              className="w-full h-full object-cover"
              loading="lazy"
              draggable="false"
            />
          ) : activeFloor?.osm_hint && venue?.lat && venue?.lng ? (
            // OSM fallback — indoor plan missing but venue has GPS. Show the
            // outdoor block from Leaflet/OSM so visitors still get context.
            // POI pins continue to overlay at (x%, y%) — this is intentional:
            // it treats the outdoor block as an approximate substrate until
            // the venue owner uploads the real indoor plan.
            <div className="relative w-full h-full" data-testid="venue-osm-fallback">
              <MapView center={[venue.lat, venue.lng]} zoom={18} height="100%" markers={[]} />
              <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-white/90 border border-stone-200 rounded-full px-3 py-1 text-[10px] font-bold text-stone-600 shadow-sm">
                {t("venue.osm_pending", "Outdoor OSM view — indoor plan pending")}
              </div>
            </div>
          ) : (
            <div className="w-full h-full grid place-items-center text-center px-6 text-stone-500 text-sm" data-testid="venue-no-floor-image">
              <div>
                <Layers size={28} className="mx-auto text-stone-300 mb-2" />
                <div className="font-bold text-stone-700">{t("venue.floor_missing", "Floor plan not uploaded yet")}</div>
              </div>
            </div>
          )}
          {/* POI markers — placed by percent coordinates */}
          {/* Wayfinding SVG — straight-line path from "here" pin to selected POI.
              MVP treats the floor plan as a flat plane; a future slice can
              add walkable-corridor waypoints via the venue owner. */}
          {(() => {
            const here = pois.find(p => p.poi_id === hereId);
            const showPath = params.get("path") === "1" && here && selected && here.poi_id !== selected.poi_id;
            if (!showPath) return null;
            const dx = selected.x - here.x, dy = selected.y - here.y;
            const dist = Math.round(Math.hypot(dx, dy) * 3);   // rough "steps"
            return (
              <svg className="absolute inset-0 w-full h-full pointer-events-none" data-testid="venue-wayfinding">
                <defs>
                  <marker id="wayArrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#E05E36" />
                  </marker>
                </defs>
                <line
                  x1={`${here.x}%`} y1={`${here.y}%`}
                  x2={`${selected.x}%`} y2={`${selected.y}%`}
                  stroke="#E05E36" strokeWidth="3" strokeDasharray="6 6"
                  strokeLinecap="round" markerEnd="url(#wayArrow)"
                />
                <foreignObject x={`${(here.x + selected.x) / 2 - 6}%`} y={`${(here.y + selected.y) / 2 - 3}%`} width="12%" height="6%">
                  <div className="bg-[#E05E36] text-white text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 shadow-md whitespace-nowrap">
                    ~{dist} steps
                  </div>
                </foreignObject>
              </svg>
            );
          })()}

          {pois.map(p => {
            const isSelected = selected?.poi_id === p.poi_id;
            const isHere     = hereId && p.poi_id === hereId;
            return (
              <button
                key={p.poi_id}
                onClick={() => {
                  setSelected(p);
                  // Fire tap analytics (best-effort, non-blocking)
                  api.post(`/venues/${slug}/pois/${p.poi_id}/tap`).catch(() => {});
                }}
                className="absolute -translate-x-1/2 -translate-y-1/2 group"
                style={{ left: `${p.x}%`, top: `${p.y}%` }}
                data-testid={`poi-${p.poi_id}`}
                aria-label={p.name}
              >
                <div className={`rounded-full grid place-items-center shadow-md transition-all ${
                  isHere ? "bg-[#E05E36] text-white w-8 h-8 ring-4 ring-[#E05E36]/30 animate-pulse"
                         : isSelected ? "bg-stone-900 text-white w-7 h-7"
                                      : "bg-white text-stone-900 w-6 h-6 hover:scale-110 border border-stone-200"
                }`}>
                  {renderPoiIcon(p)}
                </div>
                <div className={`absolute left-1/2 -translate-x-1/2 mt-1 text-[10px] font-bold whitespace-nowrap px-1.5 py-0.5 rounded ${
                  isHere ? "bg-[#E05E36] text-white" : "bg-white/90 text-stone-700 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                }`}>
                  {isHere ? t("venue.you_are_here", "You are here") : p.name}
                </div>
              </button>
            );
          })}
        </div>

        {/* Zoom controls */}
        <div className="absolute right-3 bottom-3 flex flex-col gap-1" data-testid="venue-zoom">
          <button onClick={() => setZoom(z => Math.min(3, +(z + 0.25).toFixed(2)))} className="w-9 h-9 rounded-full bg-white shadow border border-stone-200 grid place-items-center" data-testid="venue-zoom-in">
            <Plus size={14} />
          </button>
          <button onClick={() => setZoom(z => Math.max(0.5, +(z - 0.25).toFixed(2)))} className="w-9 h-9 rounded-full bg-white shadow border border-stone-200 grid place-items-center" data-testid="venue-zoom-out">
            <Minus size={14} />
          </button>
        </div>

        {/* Result count badge */}
        <div className="absolute left-3 top-3 bg-white rounded-full shadow px-3 py-1 text-[11px] font-bold text-stone-600" data-testid="venue-count">
          {pois.length} {t("venue.spots", "spots")}
        </div>
      </div>

      {/* Selected POI card */}
      {selected && (
        <PoiCard
          poi={selected}
          cats={cats}
          t={t}
          hereId={hereId}
          pathOn={params.get("path") === "1"}
          onWalk={() => setP({ path: params.get("path") === "1" ? "" : "1" })}
          onClose={() => setSelected(null)}
        />
      )}

      {/* Kiosk footer */}
      {kiosk && (
        <KioskFooter venue={venue} />
      )}

      {/* Map On Phone QR modal */}
      {showQr && (
        <QrHandoffModal t={t} onClose={() => setShowQr(false)} />
      )}
    </div>
  );
}


function LangSwitcher({ languages, active, onPick }) {
  const [open, setOpen] = useState(false);
  const activeMeta = LANG_META[active] || { flag: "🌐", label: active };
  return (
    <div className="relative" data-testid="venue-lang">
      <button
        onClick={() => setOpen(o => !o)}
        className="text-xs font-bold px-2.5 py-1.5 rounded-full border bg-white border-stone-200 inline-flex items-center gap-1"
        aria-label="Change language"
        data-testid="venue-lang-btn"
      >
        <span className="text-base leading-none">{activeMeta.flag}</span>
        <span className="uppercase tracking-wider text-[10px] text-stone-700">{active}</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-stone-200 rounded-xl shadow-md py-1 min-w-[160px] z-30" data-testid="venue-lang-menu">
          {languages.map(code => {
            const m = LANG_META[code] || { flag: "🌐", label: code };
            const on = code === active;
            return (
              <button
                key={code}
                onClick={() => { onPick(code); setOpen(false); }}
                className={`w-full text-left px-3 py-1.5 text-xs inline-flex items-center gap-2 ${on ? "font-bold text-[#E05E36]" : "text-stone-700 hover:bg-stone-50"}`}
                data-testid={`venue-lang-opt-${code}`}
              >
                <span className="text-base leading-none">{m.flag}</span> {m.label}
                <span className="ml-auto text-[10px] text-stone-400 uppercase">{code}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


function QrHandoffModal({ onClose, t }) {
  // Encode the current URL minus `?kiosk=1` so the mobile visitor picks up
  // the same floor / category / search state without the kiosk chrome.
  const url = (() => {
    const u = new URL(typeof window !== "undefined" ? window.location.href : "");
    u.searchParams.delete("kiosk");
    return u.toString();
  })();
  return (
    <div className="fixed inset-0 bg-black/60 z-[9800] grid place-items-center p-6" onClick={onClose} data-testid="venue-qr-modal">
      <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="heading font-bold text-lg text-stone-900">{t ? t("venue.map_on_phone", "Map on phone") : "Map on phone"}</div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-stone-100" aria-label="Close" data-testid="venue-qr-close">
            <X size={16} />
          </button>
        </div>
        <div className="bg-stone-50 rounded-xl p-4 grid place-items-center" data-testid="venue-qr-canvas">
          <QRCodeCanvas value={url} size={220} bgColor="#fafaf9" fgColor="#0c0a09" includeMargin={false} />
        </div>
        <div className="text-xs text-stone-500 mt-3">
          {t ? t("venue.qr_hint", "Scan to open this exact map on your phone.") : "Scan to open this exact map on your phone."}
        </div>
        <div className="text-[10px] text-stone-400 mt-1 break-all" data-testid="venue-qr-url">{url}</div>
      </div>
    </div>
  );
}


function FloorPicker({ floors, activeId, onPick }) {
  const [open, setOpen] = useState(false);
  const active = floors.find(f => f.id === activeId) || floors[0];
  return (
    <div className="relative" data-testid="venue-floor-picker">
      <button
        onClick={() => setOpen(o => !o)}
        className="text-xs font-bold px-3 py-1.5 rounded-full border bg-white border-stone-200 inline-flex items-center gap-1.5"
        data-testid="venue-floor-picker-btn"
      >
        <Layers size={12} /> {active?.label || "Floor"}
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-stone-200 rounded-xl shadow-md py-1 min-w-[180px] z-30" data-testid="venue-floor-menu">
          {floors.map(f => (
            <button
              key={f.id}
              onClick={() => { onPick(f.id); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-xs ${f.id === activeId ? "font-bold text-[#E05E36]" : "text-stone-700 hover:bg-stone-50"}`}
              data-testid={`venue-floor-opt-${f.id}`}
            >
              {f.label} <span className="text-stone-400">· {f.poi_count || 0} POIs</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function PoiCard({ poi, cats, onClose, t, hereId, pathOn, onWalk }) {
  const meta = cats.find(c => c.key === poi.category);
  const catLabel = (t && t(`poi.${poi.category}`, meta?.label)) || meta?.label || poi.category;
  const canWalk = Boolean(hereId && hereId !== poi.poi_id);
  return (
    <div className="fixed inset-x-0 bottom-0 md:inset-auto md:right-6 md:bottom-6 md:w-96 bg-white border border-stone-200 rounded-t-2xl md:rounded-2xl shadow-xl p-4 z-[9500]" data-testid="venue-poi-card">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">{catLabel}</div>
          <div className="heading font-bold text-lg text-stone-900">{poi.name}</div>
        </div>
        <button onClick={onClose} className="p-1 rounded-full hover:bg-stone-100" aria-label="Close" data-testid="venue-poi-close">
          <X size={16} />
        </button>
      </div>
      {poi.description && (
        <div className="text-sm text-stone-600 mt-2">{poi.description}</div>
      )}
      <div className="mt-3 flex gap-2 flex-wrap">
        {canWalk && (
          <button
            onClick={onWalk}
            className={`text-xs font-bold rounded-full px-3 py-1.5 inline-flex items-center gap-1 ${pathOn ? "bg-stone-900 text-white" : "bg-[#E05E36] text-white"}`}
            data-testid="venue-poi-walk"
          >
            {pathOn
              ? (t ? t("venue.hide_path", "Hide path")  : "Hide path")
              : (t ? t("venue.walk_here", "Walk here")   : "Walk here")}
          </button>
        )}
        {poi.listing_id && (
          <Link
            to={`/listing/${poi.listing_id}`}
            className="text-xs font-bold rounded-full px-3 py-1.5 border border-stone-200 text-stone-700"
            data-testid="venue-poi-listing-link"
          >
            {t ? t("venue.open_listing", "Open listing →") : "Open listing →"}
          </Link>
        )}
      </div>
    </div>
  );
}


function KioskFooter({ venue }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);
  const nice = now.toLocaleString(undefined, {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
  return (
    <div className="border-t border-stone-200 bg-stone-50 px-4 py-2 flex items-center justify-between text-xs text-stone-500" data-testid="venue-kiosk-footer">
      <div className="font-bold text-stone-700">{venue.name}</div>
      <div>{nice}</div>
    </div>
  );
}
